/**
 * Inspector panels for the Talking Heads studio: canvas gallery, selected
 * box / decoration, background, decorations list and saved templates.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  FaAlignCenter, FaArrowDown, FaArrowUp, FaCopy, FaEye, FaEyeSlash, FaTrash, FaPlus,
  FaSave, FaUpload, FaCheck, FaLock, FaLockOpen,
} from 'react-icons/fa';
import {
  MdAlignHorizontalCenter, MdAlignHorizontalLeft, MdAlignHorizontalRight,
  MdAlignVerticalBottom, MdAlignVerticalCenter, MdAlignVerticalTop,
} from 'react-icons/md';
import ThCanvasScene from './ThCanvasScene';
import { useFitSize } from './ThStage';
import { BANNER_FONTS, layerBox, layerLabel, newLayerId } from './bannerPresets';
import { IMAGE_SHAPES, imageShapeSvgPoints } from './imageShapes';
import { normalizeMediaUrl, resolveMediaUrl, uploadCompositionImage } from './uploadHelper';
import { toast } from 'react-toastify';
import {
  BG_TYPES, DECOR_KITS, FIT_MODES, FRAME_STYLES, GRADIENT_SWATCHES, LAYER_META, PATTERNS,
  buildPremade, premadeFor, orientationOf, buildFacelessPremade, facelessPremadeFor,
} from './thCanvas';
import { ColorField, Section, Segmented, SelectField, Slider, Toggle } from './studioKit';

/* ─────────────────────────────────────────────────── thumbnails ── */
export function CanvasThumb({ design, W, H, mode = 'narration' }) {
  const [ref, size] = useFitSize(W / H, 0);
  return (
    <div className="ths-thumb" ref={ref} style={{ aspectRatio: `${W} / ${H}` }}>
      {size.w > 0 && <ThCanvasScene design={design} mode={mode} W={W} H={H} dims={size} labels={false} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────── gallery ── */
export function CanvasGallery({ W, H, currentId, onApply, faceless = false, previewMode = null }) {
  const list = useMemo(() => {
    const o = orientationOf(W, H);
    return faceless
      ? facelessPremadeFor(o).map((c) => ({ ...c, design: buildFacelessPremade(c.id, W, H) }))
      : premadeFor(o).map((c) => ({ ...c, design: buildPremade(c.id, W, H) }));
  }, [W, H, faceless]);
  const [peek, setPeek] = useState(null);
  return (
    <div className="ths-gallery">
      <p className="ths-note">Pick a canvas, then make it yours — every box, colour, frame and label is editable.</p>
      <div className="ths-gallery__grid">
        {list.map((c) => (
          <button
            type="button"
            key={c.id}
            className={`ths-card ${currentId === c.id ? 'is-active' : ''}`}
            onClick={() => onApply(c.id)}
            onMouseEnter={() => setPeek(c.id)}
            onMouseLeave={() => setPeek(null)}
            onFocus={() => setPeek(c.id)}
            onBlur={() => setPeek(null)}
          >
            <CanvasThumb design={c.design} W={W} H={H} mode={previewMode || (peek === c.id ? 'clip' : 'narration')} />
            <span className="ths-card__meta">
              <strong>{c.name}</strong>
              <em>{c.tag}</em>
            </span>
            {currentId === c.id && <span className="ths-card__check"><FaCheck /></span>}
          </button>
        ))}
      </div>
      {!previewMode && <p className="ths-note ths-note--dim">Hover a card to see its clip-segment layout.</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────────── helpers ── */
function NumberField({ label, value, onChange, min, max, step = 1, suffix }) {
  const [draft, setDraft] = useState(null);
  return (
    <label className="ths-num">
      <span>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={draft ?? String(Math.round(value * 10) / 10)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft != null) {
            const n = parseFloat(draft);
            if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
          }
          setDraft(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const d = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
            onChange(Math.max(min, Math.min(max, value + d)));
          }
        }}
      />
      {suffix && <em>{suffix}</em>}
    </label>
  );
}

function AlignBar({ onAlign }) {
  const btns = [
    ['left', MdAlignHorizontalLeft, 'Align left'],
    ['hcenter', MdAlignHorizontalCenter, 'Centre horizontally'],
    ['right', MdAlignHorizontalRight, 'Align right'],
    ['top', MdAlignVerticalTop, 'Align top'],
    ['vcenter', MdAlignVerticalCenter, 'Centre vertically'],
    ['bottom', MdAlignVerticalBottom, 'Align bottom'],
  ];
  return (
    <div className="ths-align" role="group" aria-label="Align to canvas">
      {btns.map(([k, Icon, t]) => (
        <button key={k} type="button" className="st-icon-btn" title={t} aria-label={t} onClick={() => onAlign(k)}>
          <Icon />
        </button>
      ))}
    </div>
  );
}

const SHADOW_PRESETS = [
  { id: 'none', label: 'None', v: { enabled: false } },
  { id: 'soft', label: 'Soft', v: { enabled: true, blur: 56, x: 0, y: 20, spread: 0, color: '#000000', opacity: 0.5 } },
  { id: 'lifted', label: 'Lifted', v: { enabled: true, blur: 90, x: 0, y: 36, spread: -6, color: '#000000', opacity: 0.6 } },
  { id: 'glow', label: 'Glow', v: { enabled: true, blur: 46, x: 0, y: 0, spread: 2, color: '#a78bfa', opacity: 0.8 } },
  { id: 'hard', label: 'Hard', v: { enabled: true, blur: 0, x: 12, y: 12, spread: 0, color: '#000000', opacity: 1 } },
];

/* ─────────────────────────────────────────── layer (video box) ── */
export function LayerInspector({ design, layerKey, W, H, update, mode, avatarDims, onSelect }) {
  const l = design.layers[layerKey];
  const [lockAspect, setLockAspect] = useState(false);
  if (!l) {
    return <p className="ths-note">This canvas has no {LAYER_META[layerKey].label.toLowerCase()} box.</p>;
  }
  const meta = LAYER_META[layerKey];
  const set = (patch) => update((d) => ({ ...d, layers: { ...d.layers, [layerKey]: { ...d.layers[layerKey], ...patch } } }));
  const setFrame = (patch) => set({ frame: { ...l.frame, ...patch } });
  const setShadow = (patch) => set({ shadow: { ...l.shadow, ...patch } });
  const clampBox = (b) => {
    const width = Math.round(Math.max(40, Math.min(W, b.width)));
    const height = Math.round(Math.max(40, Math.min(H, b.height)));
    return {
      width,
      height,
      x: Math.round(Math.max(0, Math.min(W - width, b.x))),
      y: Math.round(Math.max(0, Math.min(H - height, b.y))),
    };
  };
  const setBox = (patch) => {
    let next = { x: l.x, y: l.y, width: l.width, height: l.height, ...patch };
    const ar = l.width / l.height;
    const lock = lockAspect || l.frame.shape === 'circle';
    if (lock && patch.width != null && patch.height == null) next.height = next.width / ar;
    if (lock && patch.height != null && patch.width == null) next.width = next.height * ar;
    next = clampBox(next);
    set(next);
  };
  const align = (k) => {
    if (k === 'left') setBox({ x: 0 });
    if (k === 'hcenter') setBox({ x: (W - l.width) / 2 });
    if (k === 'right') setBox({ x: W - l.width });
    if (k === 'top') setBox({ y: 0 });
    if (k === 'vcenter') setBox({ y: (H - l.height) / 2 });
    if (k === 'bottom') setBox({ y: H - l.height });
  };
  const sizePreset = (k) => {
    if (k === 'full') set(clampBox({ x: 0, y: 0, width: W, height: H }));
    else if (k === '169') {
      const width = Math.min(W * 0.8, (H * 0.8 * 16) / 9);
      setBox({ width, height: (width * 9) / 16, x: (W - width) / 2, y: (H - (width * 9) / 16) / 2 });
    } else if (k === 'sq') {
      const side = Math.min(l.width, l.height);
      setBox({ width: side, height: side });
    } else if (k === 'pip') {
      const width = W * (W > H ? 0.24 : 0.4);
      const height = Math.min(H * 0.42, width * 1.2);
      setBox({ width, height, x: W - width - W * 0.035, y: H - height - H * 0.05 });
    }
  };
  const fitToAvatar = () => {
    if (!avatarDims?.w) return;
    const ar = avatarDims.w / avatarDims.h;
    let width = l.width;
    let height = width / ar;
    if (height > H * 0.92) { height = H * 0.92; width = height * ar; }
    setBox({ width, height });
  };
  const visibleIn = layerKey === 'clip' ? 'clip segments' : 'narration';
  const shadowPreset = SHADOW_PRESETS.find((p) => (p.v.enabled === false ? !l.shadow.enabled
    : l.shadow.enabled && p.v.blur === l.shadow.blur && p.v.y === l.shadow.y && p.v.x === l.shadow.x && p.v.color === l.shadow.color))?.id;

  return (
    <div className="ths-panel">
      <div className="ths-panel__head">
        <span className="ths-dot" style={{ background: meta.color }} />
        <div>
          <strong>{meta.label} box</strong>
          <em>{meta.hint} · shown in {visibleIn}</em>
        </div>
        {((layerKey === 'clip') !== (mode === 'clip')) && (
          <button type="button" className="st-btn st-btn--sm" onClick={() => onSelect?.({ kind: 'layer', id: layerKey }, true)}>
            Show
          </button>
        )}
      </div>

      <Section title="Position & size">
        <div className="ths-num-grid">
          <NumberField label="X" value={l.x} min={0} max={W - l.width} onChange={(v) => setBox({ x: v })} />
          <NumberField label="Y" value={l.y} min={0} max={H - l.height} onChange={(v) => setBox({ y: v })} />
          <NumberField label="W" value={l.width} min={40} max={W} onChange={(v) => setBox({ width: v })} />
          <NumberField label="H" value={l.height} min={40} max={H} onChange={(v) => setBox({ height: v })} />
        </div>
        <div className="ths-row">
          <AlignBar onAlign={align} />
          <button
            type="button"
            className={`st-icon-btn ${lockAspect ? 'is-active' : ''}`}
            title={lockAspect ? 'Aspect locked' : 'Lock aspect ratio'}
            aria-pressed={lockAspect}
            onClick={() => setLockAspect((v) => !v)}
          >
            {lockAspect ? <FaLock /> : <FaLockOpen />}
          </button>
        </div>
        <div className="ths-chips">
          <button type="button" className="ths-chip" onClick={() => sizePreset('full')}>Full canvas</button>
          <button type="button" className="ths-chip" onClick={() => sizePreset('169')}>16:9 centred</button>
          <button type="button" className="ths-chip" onClick={() => sizePreset('sq')}>Square</button>
          {layerKey === 'avatar' && <button type="button" className="ths-chip" onClick={() => sizePreset('pip')}>PIP corner</button>}
          {layerKey === 'avatar' && avatarDims?.w && (
            <button type="button" className="ths-chip" onClick={fitToAvatar}>Fit to video {avatarDims.w}×{avatarDims.h}</button>
          )}
        </div>
        {layerKey !== 'avatar' && (
          <Segmented
            label="Video fit"
            options={FIT_MODES}
            value={layerKey === 'content' ? design.contentFit : design.clipFit}
            onChange={(v) => update((d) => ({ ...d, [layerKey === 'content' ? 'contentFit' : 'clipFit']: v }))}
          />
        )}
        {layerKey === 'avatar' && <p className="st-hint">The avatar never stretches — empty space is filled with a soft blurred copy of it.</p>}
      </Section>

      <Section title="Shape & frame">
        <Segmented
          label="Shape"
          options={[{ value: 'rect', label: 'Rectangle' }, { value: 'circle', label: 'Circle' }]}
          value={l.frame.shape}
          onChange={(v) => {
            if (v === 'circle') {
              const side = Math.min(l.width, l.height);
              set({ ...clampBox({ x: l.x + (l.width - side) / 2, y: l.y + (l.height - side) / 2, width: side, height: side }), frame: { ...l.frame, shape: 'circle' } });
            } else setFrame({ shape: 'rect' });
          }}
        />
        {l.frame.shape === 'rect' && (
          <Slider label="Corner radius" value={l.frame.radius} min={0} max={Math.round(Math.min(l.width, l.height) / 2)} step={1} unit="px" onChange={(v) => setFrame({ radius: v })} />
        )}
        <Slider label="Frame width" value={l.frame.width} min={0} max={40} step={1} unit="px" onChange={(v) => setFrame({ width: v })} />
        {l.frame.width > 0 && (
          <>
            <Segmented label="Style" options={FRAME_STYLES} value={l.frame.style} onChange={(v) => setFrame({ style: v })} />
            <div className="st-grid2">
              <ColorField label="Colour" value={l.frame.color} onChange={(v) => setFrame({ color: v })} />
              <Slider label="Opacity" value={l.frame.opacity} min={0} max={1} step={0.05} scale={100} unit="%" onChange={(v) => setFrame({ opacity: v })} />
            </div>
          </>
        )}
      </Section>

      <Section title="Shadow & glow" defaultOpen={l.shadow.enabled}>
        <div className="ths-chips">
          {SHADOW_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`ths-chip ${shadowPreset === p.id ? 'is-active' : ''}`}
              onClick={() => setShadow(p.id === 'glow' ? { ...p.v, color: l.frame.width > 0 ? l.frame.color : p.v.color } : p.v)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Toggle label="Shadow" checked={l.shadow.enabled} onChange={(v) => setShadow({ enabled: v })} />
        {l.shadow.enabled && (
          <>
            <div className="st-grid2">
              <Slider label="Blur" value={l.shadow.blur} min={0} max={200} unit="px" onChange={(v) => setShadow({ blur: v })} />
              <Slider label="Spread" value={l.shadow.spread} min={-40} max={80} unit="px" onChange={(v) => setShadow({ spread: v })} />
              <Slider label="Offset X" value={l.shadow.x} min={-120} max={120} unit="px" onChange={(v) => setShadow({ x: v })} />
              <Slider label="Offset Y" value={l.shadow.y} min={-120} max={120} unit="px" onChange={(v) => setShadow({ y: v })} />
            </div>
            <div className="st-grid2">
              <ColorField label="Colour" value={l.shadow.color} onChange={(v) => setShadow({ color: v })} />
              <Slider label="Opacity" value={l.shadow.opacity} min={0} max={1} step={0.05} scale={100} unit="%" onChange={(v) => setShadow({ opacity: v })} />
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

/* ─────────────────────────────────────────────── background ── */
export function BackgroundInspector({ design, update, onUpload, uploading, layoutKind, faceless = false }) {
  const bg = design.background;
  const set = (patch) => update((d) => ({ ...d, background: { ...d.background, ...patch } }));
  const setG = (patch) => set({ gradient: { ...bg.gradient, ...patch } });
  const setP = (patch) => set({ pattern: { ...bg.pattern, ...patch } });
  const fileRef = useRef(null);
  const blurSource = layoutKind === 'full_avatar' ? 'the avatar video' : 'the narration / clip footage';

  return (
    <div className="ths-panel">
      {layoutKind === 'full_content' && !faceless && (
        <p className="ths-warn">“Content Only” plays your footage full-screen with no compositing, so background and decorations are not rendered. Pick another canvas to use them.</p>
      )}
      <Section title="Fill">
        <Segmented options={BG_TYPES} value={bg.type} onChange={(v) => set({ type: v })} />
        {bg.type === 'gradient' && (
          <>
            <div className="ths-swatches">
              {GRADIENT_SWATCHES.map((g) => (
                <button
                  key={`${g.from}${g.to}`}
                  type="button"
                  className="ths-swatch"
                  style={{ background: `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})` }}
                  title={`${g.from} → ${g.to}`}
                  onClick={() => setG({ from: g.from, to: g.to, angle: g.angle })}
                />
              ))}
            </div>
            <div className="st-grid2">
              <ColorField label="From" value={bg.gradient.from} onChange={(v) => setG({ from: v })} />
              <ColorField label="To" value={bg.gradient.to} onChange={(v) => setG({ to: v })} />
            </div>
            <Segmented options={[{ value: 'linear', label: 'Linear' }, { value: 'radial', label: 'Radial' }]} value={bg.gradient.type} onChange={(v) => setG({ type: v })} />
            {bg.gradient.type === 'linear' && (
              <Slider label="Angle" value={bg.gradient.angle} min={0} max={360} unit="°" onChange={(v) => setG({ angle: v })} />
            )}
          </>
        )}
        {bg.type === 'solid' && <ColorField label="Colour" value={bg.color} onChange={(v) => set({ color: v })} />}
        {bg.type === 'blurred' && <p className="st-hint">A soft blurred copy of {blurSource} fills the canvas behind the boxes.</p>}
        {(bg.type === 'image' || bg.type === 'video') && (
          <div className="ths-upload">
            <button type="button" className="st-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <FaUpload /> {uploading ? 'Uploading…' : bg.url ? `Replace ${bg.type}` : `Upload ${bg.type}`}
            </button>
            {bg.url && <span className="ths-upload__ok"><FaCheck /> {bg.type} ready</span>}
            <input
              ref={fileRef}
              type="file"
              accept={bg.type === 'image' ? 'image/*' : 'video/*'}
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(f, bg.type); }}
            />
          </div>
        )}
        {['blurred', 'image', 'video'].includes(bg.type) && (
          <div className="st-grid2">
            <ColorField label="Tint" value={bg.tint.color} onChange={(v) => set({ tint: { ...bg.tint, color: v } })} />
            <Slider label="Tint strength" value={bg.tint.opacity} min={0} max={0.9} step={0.05} scale={100} unit="%" onChange={(v) => set({ tint: { ...bg.tint, opacity: v } })} />
          </div>
        )}
      </Section>

      <Section title="Pattern & vignette">
        <Segmented options={PATTERNS} value={bg.pattern.kind} onChange={(v) => setP({ kind: v })} />
        {bg.pattern.kind !== 'none' && (
          <>
            <div className="st-grid2">
              <ColorField label="Colour" value={bg.pattern.color} onChange={(v) => setP({ color: v })} />
              <Slider label="Opacity" value={bg.pattern.opacity} min={0.01} max={0.5} step={0.01} scale={100} unit="%" onChange={(v) => setP({ opacity: v })} />
              <Slider label="Spacing" value={bg.pattern.size} min={8} max={240} unit="px" onChange={(v) => setP({ size: v })} />
              <Slider label="Thickness" value={bg.pattern.thickness} min={1} max={16} unit="px" onChange={(v) => setP({ thickness: v })} />
            </div>
          </>
        )}
        <Slider label="Vignette" value={bg.vignette} min={0} max={1} step={0.05} scale={100} unit="%" onChange={(v) => set({ vignette: v })} />
      </Section>
    </div>
  );
}

/* ─────────────────────────────────────────────── decorations ── */
export function DecorList({ design, update, selectedId, onSelect, H, W }) {
  const add = (kit) => {
    const items = kit.build(H, W || (H * 16) / 9).map((l) => ({ ...l, id: newLayerId(l.type) }));
    update((d) => ({ ...d, decor: [...d.decor, ...items] }), { commit: true });
    onSelect({ kind: 'decor', id: items[items.length - 1].id });
  };
  const list = [...design.decor].sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
  return (
    <div className="ths-panel">
      <Section title="Add">
        <div className="ths-kits">
          {DECOR_KITS.map((k) => (
            <button key={k.id} type="button" className="ths-kit" onClick={() => add(k)}>
              <FaPlus aria-hidden="true" /> {k.label}
            </button>
          ))}
        </div>
      </Section>
      <Section title={`Decorations (${design.decor.length})`}>
        {list.length === 0 && <p className="st-muted">No decorations yet — add a name tag, badge or lower third above.</p>}
        <ul className="ths-decor-list">
          {list.map((l) => (
            <li key={l.id} className={selectedId === l.id ? 'is-active' : ''}>
              <button type="button" className="ths-decor-list__main" onClick={() => onSelect({ kind: 'decor', id: l.id })}>
                <span className={`ths-decor-kind ths-decor-kind--${l.type}`}>{l.type === 'text' ? 'T' : l.type === 'image' ? '◐' : '■'}</span>
                <span className="ths-decor-name">{layerLabel(l)}</span>
                <em>{l.placement === 'under' ? 'behind' : 'front'}{l.scope !== 'both' ? ` · ${l.scope}` : ''}</em>
              </button>
              <button
                type="button"
                className="st-icon-btn"
                aria-label={l.visible === false ? 'Show' : 'Hide'}
                title={l.visible === false ? 'Show' : 'Hide'}
                onClick={() => update((d) => ({ ...d, decor: d.decor.map((x) => (x.id === l.id ? { ...x, visible: x.visible === false } : x)) }), { commit: true })}
              >
                {l.visible === false ? <FaEyeSlash /> : <FaEye />}
              </button>
              <button
                type="button"
                className="st-icon-btn"
                aria-label="Delete"
                title="Delete"
                onClick={() => update((d) => ({ ...d, decor: d.decor.filter((x) => x.id !== l.id) }), { commit: true })}
              >
                <FaTrash />
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

export function DecorInspector({ design, id, update, W, H, textRef, onSelect, hideScope = false }) {
  const l = design.decor.find((x) => x.id === id);
  if (!l) return null;
  const set = (patch, opts) => update((d) => ({ ...d, decor: d.decor.map((x) => (x.id === id ? { ...x, ...patch } : x)) }), opts);
  const box = layerBox(l, { w: W, h: H });
  const setBox = (patch) => {
    const b = { ...box, ...patch };
    set({ anchor: 'center', xPct: (b.cx / W) * 100, yPct: (b.cy / H) * 100, widthPct: (b.w / W) * 100, heightPct: (b.h / H) * 100 });
  };
  const z = l.zIndex ?? 0;
  const zs = design.decor.map((x) => x.zIndex ?? 0);
  const duplicate = () => {
    const copy = { ...l, id: newLayerId(l.type), xPct: (l.xPct ?? 0) + 2, yPct: (l.yPct ?? 0) + 2, zIndex: Math.max(...zs) + 1 };
    update((d) => ({ ...d, decor: [...d.decor, copy] }), { commit: true });
    onSelect({ kind: 'decor', id: copy.id });
  };
  const fontPx = Math.round((l.fontSizePct ?? 0.05) * H);

  return (
    <div className="ths-panel">
      <div className="ths-panel__head">
        <span className="ths-dot" style={{ background: '#f59e0b' }} />
        <div>
          <strong>{layerLabel(l)}</strong>
          <em>{l.type === 'text' ? 'Text decoration' : l.type === 'image' ? 'Image' : 'Shape decoration'}</em>
        </div>
        <button type="button" className="st-icon-btn" title="Duplicate" aria-label="Duplicate" onClick={duplicate}><FaCopy /></button>
        <button
          type="button"
          className="st-icon-btn"
          title="Delete"
          aria-label="Delete"
          onClick={() => { update((d) => ({ ...d, decor: d.decor.filter((x) => x.id !== id) }), { commit: true }); onSelect(null); }}
        >
          <FaTrash />
        </button>
      </div>

      {l.type === 'text' && (
        <Section title="Text">
          <textarea
            ref={textRef}
            className="st-textarea"
            rows={2}
            value={l.text}
            onChange={(e) => set({ text: e.target.value })}
            aria-label="Text"
          />
          <div className="st-grid2">
            <SelectField label="Font" value={l.fontFamily} onChange={(v) => set({ fontFamily: v })} options={BANNER_FONTS.map((f) => ({ value: f.value, label: f.label }))} />
            <SelectField
              label="Weight"
              value={String(l.fontWeight)}
              onChange={(v) => set({ fontWeight: v })}
              options={[{ value: '400', label: 'Regular' }, { value: '700', label: 'Bold' }, { value: '800', label: 'Heavy' }]}
            />
          </div>
          <Slider label="Size" value={fontPx} min={10} max={Math.round(H * 0.2)} unit="px" onChange={(v) => set({ fontSizePct: v / H })} />
          <div className="st-grid2">
            <ColorField label="Colour" value={l.color} onChange={(v) => set({ color: v })} />
            <Slider label="Letter spacing" value={l.letterSpacing ?? 0} min={-0.1} max={1} step={0.02} onChange={(v) => set({ letterSpacing: v })} />
          </div>
          <Segmented
            label="Align"
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' }]}
            value={l.textAlign || 'center'}
            onChange={(v) => set({ textAlign: v })}
          />
          <div className="st-toggles">
            <Toggle label="UPPERCASE" checked={l.textTransform === 'uppercase'} onChange={(v) => set({ textTransform: v ? 'uppercase' : 'none' })} />
            <Toggle label="Text shadow" checked={l.shadowEnabled !== false} onChange={(v) => set({ shadowEnabled: v })} />
            <Toggle label="Pill background" checked={!!l.bgEnabled} onChange={(v) => set({ bgEnabled: v, bgOpacity: v && !(l.bgOpacity > 0) ? 1 : l.bgOpacity })} />
          </div>
          {l.bgEnabled && (
            <div className="st-grid2">
              <ColorField label="Pill colour" value={l.bgColor} onChange={(v) => set({ bgColor: v })} />
              <Slider label="Pill roundness" value={l.bgRadiusPct ?? 0.2} min={0} max={0.5} step={0.01} scale={200} unit="%" onChange={(v) => set({ bgRadiusPct: v })} />
              <Slider label="Pill opacity" value={l.bgOpacity ?? 1} min={0} max={1} step={0.05} scale={100} unit="%" onChange={(v) => set({ bgOpacity: v })} />
            </div>
          )}
        </Section>
      )}

      {l.type === 'image' && (
        <ImageDecorFields l={l} set={set} box={box} setBox={setBox} H={H} />
      )}

      {l.type === 'shape' && (
        <Section title="Shape">
          <Segmented
            options={[{ value: 'rect', label: 'Rectangle' }, { value: 'ellipse', label: 'Ellipse' }]}
            value={l.shapeKind || 'rect'}
            onChange={(v) => set({ shapeKind: v })}
          />
          <div className="st-grid2">
            <ColorField label="Fill" value={l.color} onChange={(v) => set({ color: v })} />
            {l.shapeKind !== 'ellipse' && (
              <Slider label="Roundness" value={l.borderRadiusPct ?? 0} min={0} max={0.5} step={0.01} scale={200} unit="%" onChange={(v) => set({ borderRadiusPct: v })} />
            )}
          </div>
          <div className="st-toggles">
            <Toggle label="Fill" checked={l.fillEnabled !== false} onChange={(v) => set({ fillEnabled: v })} />
            <Toggle label="Outline" checked={!!l.strokeEnabled} onChange={(v) => set({ strokeEnabled: v })} />
          </div>
          {l.strokeEnabled && (
            <div className="st-grid2">
              <ColorField label="Outline" value={l.strokeColor} onChange={(v) => set({ strokeColor: v })} />
              <Slider label="Outline width" value={Math.round((l.strokeWidthPct ?? 0.004) * H)} min={1} max={40} unit="px" onChange={(v) => set({ strokeWidthPct: v / H })} />
            </div>
          )}
        </Section>
      )}

      <Section title="Placement">
        <div className="ths-num-grid">
          <NumberField label="X" value={Math.round(box.cx - box.w / 2)} min={-W} max={W * 2} onChange={(v) => setBox({ cx: v + box.w / 2 })} />
          <NumberField label="Y" value={Math.round(box.cy - box.h / 2)} min={-H} max={H * 2} onChange={(v) => setBox({ cy: v + box.h / 2 })} />
          <NumberField label="W" value={Math.round(box.w)} min={4} max={W * 2} onChange={(v) => setBox({ w: v })} />
          <NumberField label="H" value={Math.round(box.h)} min={4} max={H * 2} onChange={(v) => setBox({ h: v })} />
        </div>
        <div className="ths-row">
          <AlignBar
            onAlign={(k) => {
              if (k === 'left') setBox({ cx: box.w / 2 });
              if (k === 'hcenter') setBox({ cx: W / 2 });
              if (k === 'right') setBox({ cx: W - box.w / 2 });
              if (k === 'top') setBox({ cy: box.h / 2 });
              if (k === 'vcenter') setBox({ cy: H / 2 });
              if (k === 'bottom') setBox({ cy: H - box.h / 2 });
            }}
          />
          <button type="button" className="st-icon-btn" title="Centre on canvas" aria-label="Centre on canvas" onClick={() => setBox({ cx: W / 2, cy: H / 2 })}><FaAlignCenter /></button>
        </div>
        <div className="st-grid2">
          <Slider label="Rotation" value={l.rotation ?? 0} min={-180} max={180} unit="°" onChange={(v) => set({ rotation: v })} />
          <Slider label="Opacity" value={l.opacity ?? 1} min={0} max={1} step={0.05} scale={100} unit="%" onChange={(v) => set({ opacity: v })} />
        </div>
        <Segmented
          label="Layer"
          options={[{ value: 'under', label: 'Behind videos' }, { value: 'over', label: 'In front' }]}
          value={l.placement}
          onChange={(v) => set({ placement: v }, { commit: true })}
        />
        {!hideScope && (
          <Segmented
            label="Show during"
            options={[{ value: 'both', label: 'Everything' }, { value: 'narration', label: 'Narration' }, { value: 'clip', label: 'Clips' }]}
            value={l.scope}
            onChange={(v) => set({ scope: v }, { commit: true })}
          />
        )}
        <div className="ths-row">
          <button type="button" className="st-btn st-btn--sm" onClick={() => set({ zIndex: Math.max(...zs) + 1 }, { commit: true })} disabled={z >= Math.max(...zs)}>
            <FaArrowUp /> Bring to front
          </button>
          <button type="button" className="st-btn st-btn--sm" onClick={() => set({ zIndex: Math.min(...zs) - 1 }, { commit: true })} disabled={z <= Math.min(...zs)}>
            <FaArrowDown /> Send back
          </button>
        </div>
      </Section>
    </div>
  );
}

/* ─────────────────────────────────────── image decoration ── */
function ShapeIcon({ kind }) {
  const pts = imageShapeSvgPoints(kind, 20, 20);
  return (
    <svg width="20" height="20" viewBox="-1 -1 22 22" aria-hidden="true">
      {pts ? <polygon points={pts} fill="currentColor" /> : <rect x="0" y="0" width="20" height="20" fill="currentColor" />}
    </svg>
  );
}

function ImageDecorFields({ l, set, box, setBox, H }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const value = l.imageUrl || '';
  const preview = resolveMediaUrl(value);
  const shape = l.imageShape || 'rect';

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setUploading(true);
    try {
      let token;
      try { token = JSON.parse(localStorage.getItem('user') || 'null')?.token; } catch { token = undefined; }
      const url = await uploadCompositionImage(file, token);
      if (!url) throw new Error('no url');
      set({ imageUrl: url }, { commit: true });
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed — paste the image URL instead');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Section title="Image">
      <div
        className="st-field st-upload"
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
      >
        <button
          type="button"
          className={`st-upload__drop ${preview ? 'has-image' : ''}`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {preview ? <img src={preview} alt="" draggable={false} /> : null}
          <span>{uploading ? 'Uploading…' : preview ? 'Replace image' : 'Click or drop an image'}</span>
        </button>
        <input
          className="st-input"
          type="text"
          placeholder="…or paste an image URL"
          value={value}
          onChange={(e) => set({ imageUrl: e.target.value })}
          onBlur={() => { const n = normalizeMediaUrl(value); if (n && n !== value) set({ imageUrl: n }, { commit: true }); }}
        />
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>

      <div className="st-field">
        <span className="st-field__label">Shape</span>
        <div className="ths-imgshapes" role="radiogroup" aria-label="Image shape">
          {IMAGE_SHAPES.map((s) => (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={shape === s.value}
              title={s.label}
              className={`ths-imgshape ${shape === s.value ? 'is-active' : ''}`}
              onClick={() => set({ imageShape: s.value, shape: undefined }, { commit: true })}
            >
              <ShapeIcon kind={s.value} />
              <span>{s.label}</span>
            </button>
          ))}
        </div>
        {shape !== 'rect' && Math.abs(box.w - box.h) > 2 && (
          <button type="button" className="st-btn st-btn--sm" onClick={() => setBox({ h: box.w })}>
            Make it even (square box)
          </button>
        )}
      </div>

      {shape === 'rect' && (
        <Slider label="Roundness" value={l.borderRadiusPct ?? 0} min={0} max={0.5} step={0.01} scale={200} unit="%" onChange={(v) => set({ borderRadiusPct: v })} />
      )}
      <Segmented
        label="Fit"
        options={[{ value: 'cover', label: 'Fill the shape' }, { value: 'contain', label: 'Whole picture' }]}
        value={l.objectFit || 'cover'}
        onChange={(v) => set({ objectFit: v }, { commit: true })}
      />
      {(l.objectFit || 'cover') === 'cover' && (
        <div className="st-grid2">
          <Slider label="Focus ↔" value={l.imageFocusXPct ?? 50} min={0} max={100} unit="%" onChange={(v) => set({ imageFocusXPct: v })} />
          <Slider label="Focus ↕" value={l.imageFocusYPct ?? 50} min={0} max={100} unit="%" onChange={(v) => set({ imageFocusYPct: v })} />
        </div>
      )}
      <div className="st-grid2">
        <Slider label="Border" value={l.borderWidth ?? 0} min={0} max={Math.round(H * 0.03)} unit="px" onChange={(v) => set({ borderWidth: v })} />
        <ColorField label="Border colour" value={l.borderColor || '#ffffff'} onChange={(v) => set({ borderColor: v })} />
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────── templates ── */
export function TemplatesPanel({
  templates, loading, activeId, name, setName, onSave, onSaveNew, onLoad, onDelete, toDesign, layoutConfig,
}) {
  return (
    <div className="ths-panel">
      <Section title="Save this canvas">
        <div className="ths-save">
          <input
            type="text"
            className="st-input"
            placeholder="Template name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSave(); }}
            aria-label="Template name"
          />
          <button type="button" className="st-btn st-btn--primary" onClick={onSave}>
            <FaSave /> {activeId ? 'Update' : 'Save'}
          </button>
          {activeId && (
            <button type="button" className="st-btn" onClick={onSaveNew}>Save as new</button>
          )}
        </div>
        <p className="st-hint">Templates are saved to your account and work on every DreamFace job.</p>
      </Section>
      <Section title={`My canvases${templates.length ? ` (${templates.length})` : ''}`}>
        {loading && <p className="st-muted">Loading…</p>}
        {!loading && templates.length === 0 && <p className="st-muted">Nothing saved yet.</p>}
        <div className="ths-gallery__grid">
          {templates.map((t) => {
            const parsed = toDesign(t);
            if (!parsed) return null;
            return (
              <div key={t._id} className={`ths-card ths-card--tpl ${activeId === t._id ? 'is-active' : ''}`}>
                <button type="button" className="ths-card__hit" onClick={() => onLoad(t)} aria-label={`Load ${t.name}`}>
                  <CanvasThumb design={parsed.design} W={parsed.W} H={parsed.H} />
                  <span className="ths-card__meta">
                    <strong>{t.name || 'Untitled'}</strong>
                    <em>{parsed.label}</em>
                  </span>
                </button>
                <button type="button" className="st-icon-btn ths-card__del" title="Delete" aria-label={`Delete ${t.name}`} onClick={() => onDelete(t)}>
                  <FaTrash />
                </button>
              </div>
            );
          })}
        </div>
      </Section>
      <Section title="Renderer config" defaultOpen={false}>
        <pre className="ths-config">{JSON.stringify(layoutConfig, null, 2)}</pre>
      </Section>
    </div>
  );
}
